import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IonIcon } from '@ionic/react';
import { restaurantOutline } from 'ionicons/icons';
import { useListings } from '../../hooks/useListings';
import { useListingsOrders } from '../../hooks/useOrders';
import { useAuthStore } from '../../store/useAuthStore';
import { useRootFontScale } from '../../hooks/useRootFontScale';
import type { DishListing, ListingStatus } from '../../types/listing';
import type { OrderStatus } from '../../types/order';
import type { ListingOrderSummary } from '../../services/orderService';
import { formatMethodWindows } from '../../utils/timeWindow';
import { ListingOrderStepTally, type StepCounts } from '../orders/ListingOrderStepTally';

// Statuses that keep a listing out of this panel on their own. Everything else
// (active, scheduled, sold_out) is a live listing whose orders the chef may
// still need to manage — a sold-out listing keeps its row so the chef can work
// through the orders it already took.
const HIDDEN_STATUSES: ListingStatus[] = ['expired', 'canceled', 'draft'];

/**
 * A listing earns a row if it's still live, OR if it has orders in flight.
 *
 * The second clause is not redundant. This panel is the chef's work queue, and
 * a work queue keyed purely on listing lifecycle drops work that outlives its
 * listing. `expired` is the case that bites: the server's expiry cascade
 * deliberately spares `ready_to_pickup` orders so the chef can still complete
 * or no-show them, so an expired listing can be carrying orders that nothing
 * else will ever resolve. Hiding it stranded the order — the eater's own panel
 * keys off order status and kept showing it, with no matching surface on the
 * chef's side to act from.
 *
 * `canceled` doesn't hit this path in practice (its cascade sweeps every
 * non-terminal order, including ready_to_pickup, so nothing survives to count)
 * and `draft` can't have orders at all. The check is written against the count
 * rather than against `expired` specifically so it stays correct if either of
 * those cascades changes.
 */
const isQualifyingListing = (l: DishListing): boolean =>
  !HIDDEN_STATUSES.includes(l.status) || (l.activeOrderCount ?? 0) > 0;

// Order status → tally bucket. Pre-payment (held/confirmed) and terminal
// non-fulfilled states (canceled/abandoned/expired/no_show) map to null and
// are ignored entirely.
function bucketOf(status: OrderStatus): keyof StepCounts | null {
  switch (status) {
    case 'paid':
    case 'received':
      return 'paid';
    case 'accepted':
      return 'accepted';
    case 'ready_to_pickup':
      return 'ready';
    case 'completed':
      return 'completed';
    default:
      return null;
  }
}

function tallyOrders(orders: ListingOrderSummary[]): StepCounts {
  const counts: StepCounts = { paid: 0, accepted: 0, ready: 0, completed: 0 };
  for (const o of orders) {
    const bucket = bucketOf(o.status as OrderStatus);
    if (bucket) counts[bucket] += 1;
  }
  return counts;
}

/** Timestamp (ms) of a listing's window start, or +Infinity when unset — so
 * window-less listings sort to the end rather than jumping to the front. */
function windowStartMs(l: DishListing): number {
  const start = l.timeWindow?.start;
  return start ? new Date(start).getTime() : Number.POSITIVE_INFINITY;
}

/**
 * A chef-facing summary that sits at the top of the home feed (just under the
 * publish button). One row per live listing — earliest pickup window first —
 * showing how many orders are active and — once the listing's first order has
 * actually come in — a per-step breadcrumb tally. Renders nothing for
 * non-chefs / anyone without a qualifying listing.
 */
export const ActiveListingsOrdersPanel: React.FC = () => {
  const { t } = useTranslation(['home', 'orders']);
  const history = useHistory();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: listings } = useListings(isAuthenticated);
  // At large text sizes the side-by-side left/right columns squeeze the title
  // into a near-useless truncation — above this scale the right info (portions
  // + window) drops below the left info instead. Mirrors the text-scale cutoffs
  // used in OrderCard / OrderStatusBreadcrumb.
  const stackInfo = useRootFontScale() >= 1.15;

  const activeListings = useMemo(() => {
    if (!listings) return [];
    return listings
      .filter(isQualifyingListing)
      .sort((a, b) => windowStartMs(a) - windowStartMs(b));
  }, [listings]);

  const listingIds = useMemo(() => activeListings.map((l) => l.id), [activeListings]);
  const orderQueries = useListingsOrders(listingIds);

  if (activeListings.length === 0) return null;

  return (
    <div className="border-b border-border">
      <ul className="divide-y divide-border">
        {activeListings.map((listing, i) => {
          const orders = orderQueries[i]?.data ?? [];
          const counts = tallyOrders(orders);
          const activeCount = counts.paid + counts.accepted + counts.ready;
          // The step tally only means something once a real order has landed:
          // until one has been paid for, every column reads 0 and the breadcrumb
          // is just decoration. Pre-payment orders (held/confirmed) bucket to
          // null in `tallyOrders`, so they correctly don't count as "in" yet.
          const hasOrders = activeCount + counts.completed > 0;
          const windows = formatMethodWindows(listing);
          // Portions + window: right-justified beside the title at normal text,
          // and still right-justified (just stacked below) at large text. The
          // `text-right` on the <p>s themselves handles the stacked case, where
          // they're full-width block children of the flex-1 text column.
          // Null-checked because the field is absent whenever the listing has
          // no quantity (see DishListing.itemsRemaining). An undefined count
          // matches no plural form, so i18next falls back to the base key —
          // which does not exist here, only `_one`/`_other` — and prints
          // "listerOrders.portionsRemaining" into the row. Drop the line
          // instead: a portion count we do not have is not worth a broken one.
          const portionsEl =
            listing.itemsRemaining != null ? (
              <p className="whitespace-nowrap text-right text-sm font-medium text-foreground">
                {t('listerOrders.portionsRemaining', { count: listing.itemsRemaining })}
              </p>
            ) : null;
          // One line per method window, named when there are two: the envelope
          // would merge tonight's drop-off and tomorrow's pickup into one span.
          const windowEl = windows.length > 0 ? (
            <>
              {windows.map((w) => (
                <p key={w.method ?? 'all'} className="whitespace-nowrap text-right text-xs text-muted-foreground">
                  {windows.length > 1 && w.method
                    ? `${t(`orders:card.fulfillment.${w.method}`)} ${w.label}`
                    : w.label}
                </p>
              ))}
            </>
          ) : null;
          return (
            <li key={listing.id}>
              <button
                type="button"
                onClick={() => history.push(`/listing/${listing.id}`)}
                className="flex w-full flex-col gap-2 py-3 text-left active:opacity-80"
              >
                <div className={`flex gap-3 ${stackInfo ? 'items-start' : 'items-center'}`}>
                  {/* Thumbnail + role badge — the chef-hat/restaurant corner
                      badge marks this as the caller's own listing, distinguishing
                      it from a buyer "your order" row (bag badge) when both panels
                      stack. */}
                  <div className="relative flex-shrink-0">
                    {listing.thumbnailUrl || listing.imageUrl ? (
                      <img
                        src={listing.thumbnailUrl ?? listing.imageUrl ?? ''}
                        alt=""
                        className="h-11 w-11 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="h-11 w-11 rounded-lg bg-muted" />
                    )}
                    <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-brand-primary">
                      <IonIcon icon={restaurantOutline} className="text-[11px] text-white" />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {listing.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('listerOrders.activeOrders', { count: activeCount })}
                    </p>
                    {stackInfo && (
                      <>
                        {portionsEl}
                        {windowEl}
                      </>
                    )}
                  </div>
                  {!stackInfo && (
                    <div className="flex-shrink-0">
                      {portionsEl}
                      {windowEl}
                    </div>
                  )}
                </div>
                {hasOrders && (
                  <ListingOrderStepTally
                    counts={counts}
                    readyIsDropoff={
                      counts.ready > 0 &&
                      orders.every((o) => o.status !== 'ready_to_pickup' || o.fulfillmentMethod === 'dropoff')
                    }
                  />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
